import { performanceDiagnostics } from '@/diagnostics/browserDiagnostics';
import {
  authorities,
  orderedEdit,
  orderedEditResult,
} from '@/simulation/native/authority';
import { makeAutoObservable } from 'mobx';
import type {
  ICircuitEditorModel,
  ICircuitModel,
  IHistoryModel,
} from '@/interfaces/models';
import { CircuitType } from '@/schemas/common';
import { Action } from '@/models/HistoryModel';
import { TileType } from '@/models/TileTypes';
import { extensionCatalog } from '@/extensions/catalog';
import { combineActions } from '@/actions/combineActions';

const logicCircuitTiles = [
  TileType.Delay,
  TileType.Diode,
  TileType.Not,
  TileType.Wire,
  TileType.BundleInterface,
  TileType.SingleInterface,
  TileType.MultiInterface,
];

// TODO: Add structure tiles
const structureCircuitTiles = [
  TileType.SingleInterface,
  TileType.MultiInterface,
  TileType.Structure,
];

export const placeableTilesMap = {
  [CircuitType.ChipLogic]: logicCircuitTiles,
  [CircuitType.ChipStructure]: structureCircuitTiles,
};

export class CircuitEditorModel implements ICircuitEditorModel {
  constructor(
    public circuitModel: ICircuitModel,
    public historyModel: IHistoryModel,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  startTransaction(description: string) {
    return orderedEdit(this.circuitModel, () =>
      this.historyModel.startTransaction(description),
    );
  }

  endTransaction() {
    return orderedEdit(this.circuitModel, () => this.historyModel.endTransaction());
  }

  runAction(action: Action, description: string) {
    if (action.isNoop) return;
    const operation = () => {
      const timing = performanceDiagnostics.start('editor-sync');
      const wasTransactionActive = !!this.historyModel.currentTransaction;
      if (!wasTransactionActive) this.historyModel.startTransaction(description);
      try {
        combineActions([action], description).do();
        this.historyModel.add(action);
        if (!wasTransactionActive) this.historyModel.endTransaction();
      } catch (error) {
        if (!wasTransactionActive) this.historyModel.cancelTransaction();
        throw error;
      } finally {
        performanceDiagnostics.finish('editor-sync', timing);
      }
    };
    const owner = authorities.get(this.circuitModel);
    // Synchronous inside an established boundary so compound edits can roll back.
    if (!owner || owner.executingNativeEdit) return operation();
    const timing = performanceDiagnostics.start('editor-worker');
    const edit = orderedEditResult(this.circuitModel, operation);
    const completion =
      timing === undefined
        ? edit
        : edit.finally(() => performanceDiagnostics.finish('editor-worker', timing));
    // UI gestures may intentionally ignore the returned promise. API callers await it.
    void completion.catch((error) => owner.fail(error));
    return completion;
  }

  get placeableTiles() {
    return [
      ...placeableTilesMap[this.circuitModel.circuitType],
      ...extensionCatalog.tiles(this.circuitModel.circuitType),
    ];
  }
}
